import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import styles from './index.module.scss';
import { setSelectedFilters } from '../../../store/market/actions';
import { selectedFiltersSelector } from '../../../store/market/selectors';
import DropDown from '../DropDown';
import {
  NEW_OR_OLD_HOUSING,
  ROOMS,
  smallIndent,
  SORTED_HOUSING,
} from '../../../utils/constants';

const Filters = ({ sortOption, setSortOption }) => {
  const dispatch = useDispatch();
  const selectedFilters = useSelector(selectedFiltersSelector);
  const [rooms, setRooms] = useState(
    selectedFilters.rooms?.length ? `${selectedFilters.rooms} Zimmer` : '',
  );
  const [priceFrom, setPriceFrom] = useState(
    selectedFilters.priceFrom?.length ? selectedFilters.priceFrom : '',
  );
  const [priceTo, setPriceTo] = useState(
    selectedFilters.priceTo?.length ? selectedFilters.priceTo : '',
  );
  const [newOrOld, setNewOrOld] = useState(
    selectedFilters.value?.length ? selectedFilters.value : '',
  );

  const handleRoomsChange = useCallback(
    (item) => {
      setRooms(item.text);
      if (item.text === rooms) {
        setRooms(item.text);
      } else if (item?.value) {
        // operator example: Rooms = 1 => Will look for Rooms[GTE]=1 & Rooms[LT]=2 / Rooms 5+ has no LT
        dispatch(
          setSelectedFilters({
            rooms: item.value,
            selected: true,
            operator: 'filter[numberOfRooms][lt]',
          }),
        );
        if (item.value === '5+') {
          setRooms('5+ Zimmer');
          dispatch(
            setSelectedFilters({
              rooms: '5',
              selected: true,
              operator: 'filter[numberOfRooms][gte]',
            }),
          );
        }
      } else {
        dispatch(
          setSelectedFilters({
            rooms: '',
            selected: true,
            operator: 'filter[numberOfRooms][lt]',
          }),
        );
      }
    },
    [rooms, dispatch],
  );

  const handlePriceFromChange = useCallback(
    (e) => {
      if (e.target?.value) {
        setPriceFrom(e.target?.value);
      } else {
        setPriceFrom('');
        dispatch(setSelectedFilters({ priceFrom: null, selected: false }));
      }
    },
    [dispatch],
  );

  const handlePriceFromBlur = useCallback(() => {
    // Trigger search after having 1 input AND clicking outside.
    if (priceFrom === selectedFilters.priceFrom) {
      return;
    }
    dispatch(setSelectedFilters({ priceFrom, selected: true }));
  }, [selectedFilters, priceFrom, dispatch]);

  const handlePriceToChange = useCallback(
    (e) => {
      if (e.target?.value) {
        setPriceTo(e.target.value);
      } else {
        setPriceTo('');
        dispatch(setSelectedFilters({ priceTo: '', selected: true }));
      }
    },
    [dispatch],
  );

  const handlePriceToBlur = useCallback(() => {
    // Trigger search after having 1 input AND clicking outside.
    if (priceTo === selectedFilters.priceTo) {
      return;
    }
    dispatch(setSelectedFilters({ priceTo, selected: true }));
  }, [selectedFilters, priceTo, dispatch]);

  const handleNewOrOldChange = useCallback(
    (item) => {
      setNewOrOld(item.value);
      if (item.value === newOrOld) {
        setNewOrOld(item.value);
      } else if (item?.value) {
        if (item.value === 'Neubau') {
          setNewOrOld(item.value);
          dispatch(
            setSelectedFilters({
              isNewHousing: true,
              selected: true,
              value: item.value,
            }),
          );
        } else if (item.value === 'Neubau und Bestand') {
          setNewOrOld(item.value);
          dispatch(
            setSelectedFilters({
              isNewHousing: null,
              selected: true,
              value: item.value,
            }),
          );
        } else {
          setNewOrOld(item.value);
          dispatch(
            setSelectedFilters({
              isNewHousing: false,
              selected: true,
              value: item.value,
            }),
          );
        }
      } else {
        dispatch(
          setSelectedFilters({
            isNewHousing: null,
            selected: true,
            value: null,
          }),
        );
      }
    },
    [newOrOld, dispatch],
  );

  const handleSortedChange = useCallback(
    (item) => {
      if (item.value === sortOption) return;
      setSortOption(item.value);
      dispatch(setSelectedFilters({ sortOption: item.value }));
    },
    [dispatch, setSortOption, sortOption],
  );

  return (
    <div
      style={{ paddingTop: '2.5rem', paddingBottom: `${smallIndent - 0.5}rem` }}
    >
      <p className="g-color-gray-dark-v3 text-left g-font-size-18 g-pb-2 mb-0">
        Filter
      </p>
      <div className={classNames(styles.filterContainer)}>
        <div>
          <DropDown
            label="Zimmer"
            value={rooms}
            handleChange={handleRoomsChange}
            options={ROOMS}
          />
        </div>
        <DropDown price={[priceFrom, priceTo]}>
          <div className={styles.priceBox}>
            <div>
              <input
                placeholder="Preis ab:"
                onBlur={handlePriceFromBlur}
                onChange={handlePriceFromChange}
                value={priceFrom}
                name="price ab"
                type="number"
                data-cy="price-show1"
              />
            </div>
            <span>-</span>
            <div>
              <input
                placeholder="Preis bis:"
                onBlur={handlePriceToBlur}
                onChange={handlePriceToChange}
                value={priceTo}
                name="price bis"
                type="number"
                data-cy="price-show2"
              />
            </div>
          </div>
        </DropDown>
        <div>
          <DropDown
            label="Zustand"
            value={newOrOld}
            handleChange={handleNewOrOldChange}
            options={NEW_OR_OLD_HOUSING}
          />
        </div>
        <div>
          <DropDown
            label="Sortierung"
            value={sortOption}
            handleChange={handleSortedChange}
            options={SORTED_HOUSING}
          />
        </div>
      </div>
    </div>
  );
};

Filters.propTypes = {
  sortOption: PropTypes.string,
  setSortOption: PropTypes.func,
};

Filters.defaultProps = {
  sortOption: 'Wohnlage absteigend',
  setSortOption: () => '',
};

export default Filters;
